import { AlertOctagon } from "lucide-react";
import { fmt2 } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

/**
 * Dialog used on Packing & Labels to write off damaged / spilled packs
 * from a finished-inventory row. Opens when `wasteFor` is truthy.
 */
export default function PackingWasteDialog({
  wasteFor,
  wasteForm,
  setWasteForm,
  onClose,
  onSubmit,
}) {
  return (
    <Dialog open={!!wasteFor} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent data-testid="packing-waste-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <AlertOctagon className="h-5 w-5 text-rose-600" /> Record packing waste
          </DialogTitle>
        </DialogHeader>
        {wasteFor && (
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="rounded-lg bg-stone-50 dark:bg-stone-800 p-3 text-sm">
              <span className="font-mono">{wasteFor.label_code}</span> ·{" "}
              {wasteFor.recipe_name} ·{" "}
              <span className="tnum font-semibold">{fmt2(wasteFor.remaining_packs)}</span>{" "}
              {wasteFor.pack_unit} remaining
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Packs wasted</Label>
                <Input
                  type="number"
                  min="1"
                  max={wasteFor.remaining_packs}
                  className="h-12 mt-1"
                  value={wasteForm.quantity}
                  onChange={(e) => setWasteForm({ ...wasteForm, quantity: e.target.value })}
                  required
                  data-testid="packing-waste-qty"
                />
              </div>
              <div>
                <Label>Reason</Label>
                <Select
                  value={wasteForm.reason}
                  onValueChange={(v) => setWasteForm({ ...wasteForm, reason: v })}
                >
                  <SelectTrigger className="h-12 mt-1" data-testid="packing-waste-reason">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Packing waste">Packing waste</SelectItem>
                    <SelectItem value="Damaged in packing">Damaged in packing</SelectItem>
                    <SelectItem value="Misprint / label error">Misprint / label error</SelectItem>
                    <SelectItem value="Quality reject">Quality reject</SelectItem>
                    <SelectItem value="Spillage">Spillage</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                rows={2}
                value={wasteForm.notes}
                onChange={(e) => setWasteForm({ ...wasteForm, notes: e.target.value })}
                data-testid="packing-waste-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              <Button type="submit" className="h-12 px-6" data-testid="packing-waste-submit">
                Send to waste log
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
